/* eslint-disable complexity */
import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {Button, Card, Loader, Text, useThemeValue} from '@gravity-ui/uikit';
import {useBooleanState} from '@yandex-data-ui/cloud-components';
import {Theme} from 'common/constants';
import {ValidationType} from 'common/constants/survey';
import {Success} from 'common/models/success';
import {SurveyValue, SurveyValues} from 'common/models/survey';
import {SubmitError} from 'common/models/system';
import {isEmpty, isNil} from 'lodash';
import {FormProvider, useForm} from 'react-hook-form';
import {createSearchParams, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import MarkdownText from 'ui/components/MarkdownText';
import {MessageType} from 'ui/constants/MessageType';
import {FooterContext} from 'ui/contexts/Footer';
import useConditions from 'ui/hooks/useConditions';
import useDataWatch from 'ui/hooks/useDataWatch';
import useDocumentTitle from 'ui/hooks/useDocumentTitle';
import usePersistForm from 'ui/hooks/usePersistForm';
import usePrepareData from 'ui/hooks/usePrepareData';
import useScript from 'ui/hooks/useScript';
import useStyles from 'ui/hooks/useStyles';
import i18n from 'ui/i18n';
import {actions, selectors, useAppDispatch, useAppSelector} from 'ui/store';
import focusByNames from 'ui/utils/focusByNames';
import getDefaultValues from 'ui/utils/getDefaultValues';
import {getParsedField, getSearchParamsValues} from 'ui/utils/getSearchParamsValues';
import getSeriesQuestionSlug from 'ui/utils/getSeriesQuestionSlug';
import getSurveyPath from 'ui/utils/getSurveyPath';
import * as Metrika from 'ui/utils/metrika';
import {parsePostMessageData} from 'ui/utils/parsePostMessageData';
import * as PostMessage from 'ui/utils/postMessages';
import redirectToExternalUrl from 'ui/utils/redirectToExternalUrl';
import {errorMessageByType} from 'ui/utils/validations';

import {
    cnSurveyPage,
    embedScriptUrl,
    formSettings,
    iframe,
    mobile,
    preview,
} from './SurveyPage.constants';
import {
    findErrorPage,
    getNextPage,
    getQuestionTypeMap,
    getSurveyId,
    hasRequiredQuestion,
    prepareValuesForSubmit,
    renderItem,
} from './SurveyPage.utils';

import './SurveyPage.scss';

const {selectSurvey} = selectors;
const {prepareSurvey, postSurvey} = actions;

const SurveyPage: FC = () => {
    const {surveyId = ''} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useThemeValue();

    const dispatch = useAppDispatch();
    const prepared = usePrepareData(
        [
            prepareSurvey({
                surveyId: getSurveyId(surveyId),
                iframe,
                key: searchParams.get('key') ?? undefined,
            }),
        ],
        dispatch,
    );
    const survey = useAppSelector(selectSurvey);

    useDocumentTitle(survey.name);

    const methods = useForm<SurveyValues>(formSettings);
    const [saving, startSaving, endSaving] = useBooleanState(false);
    //to scroll and focus once per submission
    const [canFocus, setCanFocus] = useState(true);
    const contentRef = useRef<HTMLDivElement>(null);
    const startMetrikaSended = useRef(false);
    const userCounters = useRef<Metrika.UserCounter[]>([]);

    const currentPageNumber = Number(searchParams.get('page')) || 1;
    const page = survey && survey.pages && survey.pages[currentPageNumber - 1];
    const questionTypeMap = useMemo(() => getQuestionTypeMap(survey), [survey]);
    const hasRequired = useMemo(() => hasRequiredQuestion(survey), [survey]);
    const searchParamsValues = useMemo(
        () => getSearchParamsValues(searchParams, questionTypeMap),
        [searchParams, questionTypeMap],
    );
    const questionsStats = useMemo(() => Metrika.getQuestionsStats(survey), [survey]);

    const [data, watchItems] = useDataWatch(methods.control, survey);
    const matchConditions = useConditions(data);

    const [localStorageValues, removeLocalStorageValues] = usePersistForm(
        surveyId,
        methods.control,
        !preview,
    );

    const {setFooter} = useContext(FooterContext);

    useScript(embedScriptUrl);

    useStyles(survey.styles);

    useLayoutEffect(() => {
        if (iframe && prepared && !isNil(survey.footer) && setFooter) {
            setFooter(survey.footer);
        }
    }, [prepared, setFooter, survey]);

    useEffect(() => {
        if (!prepared) {
            return;
        }
        if (iframe) {
            PostMessage.ping();
            PostMessage.sendPageInfo(preview ? 'preview' : 'survey-iframe');
        }
        if (questionsStats && userCounters.current.length === 0) {
            userCounters.current = Metrika.surveyPage(survey, questionsStats);
        }
    }, [prepared, questionsStats, survey]);

    useEffect(() => {
        if (
            !startMetrikaSended.current &&
            methods.formState.isDirty &&
            prepared &&
            questionsStats
        ) {
            Metrika.startChange(questionsStats, userCounters.current);
            startMetrikaSended.current = true;
        }
    }, [methods.formState.isDirty, prepared, survey, questionsStats]);

    useEffect(() => {
        if (isEmpty(methods.getValues())) {
            const initialValues = getDefaultValues(survey, searchParamsValues, localStorageValues);
            methods.reset(initialValues);
        }
    }, [localStorageValues, methods, searchParamsValues, survey]);

    useEffect(() => {
        const setValue = ({data}: MessageEvent) => {
            const parsedData = parsePostMessageData(data);
            // https://docs.yandex-team.ru/cloud/forms/postmessage#sec_format
            if (parsedData?.message !== MessageType.SetQuestionValue) {
                return;
            }
            const field = getParsedField(parsedData.slug, parsedData.value, questionTypeMap);
            if (questionTypeMap[parsedData.slug]) {
                Metrika.defaultValues(
                    Metrika.DefaultValueType.Embed,
                    questionTypeMap[parsedData.slug],
                );
            }
            Object.entries(field).forEach(([key, value]) => {
                if (key && value) {
                    methods.setValue(key, value as SurveyValue);
                }
            });
        };

        if (iframe) {
            window.addEventListener('message', setValue);
        }
        return () => window.removeEventListener('message', setValue);
    }, [methods, questionTypeMap]);

    useEffect(() => {
        if (methods.formState.errors && canFocus) {
            const names: string[] = [];
            Object.entries(methods.formState.errors).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((series, index) => {
                        Object.keys(series).forEach((question) => {
                            names.push(getSeriesQuestionSlug(key, index, question));
                        });
                    });
                } else {
                    names.push(key);
                }
            });
            const focused = focusByNames(names);
            if (focused) {
                setCanFocus(false);
            }
        }
    }, [methods.formState, canFocus]);

    const setPage = useCallback(
        (pageNumber: number) => {
            setSearchParams((currentParams) => {
                currentParams.set('page', String(pageNumber));
                return currentParams;
            });
        },
        [setSearchParams],
    );

    const onSubmit = (values: SurveyValues) => {
        if (preview) {
            return;
        }
        startSaving();
        setCanFocus(true);
        dispatch(
            postSurvey({
                surveyId,
                values: prepareValuesForSubmit(values, survey, matchConditions),
                parent: document.referrer,
                key: searchParams.get('key') ?? undefined,
                dryRun: Boolean(searchParams.get('dry_run')),
            }),
        )
            .unwrap()
            .then((result: void | Success) => {
                if (questionsStats) {
                    Metrika.submit(questionsStats, userCounters.current);
                }
                methods.reset();
                removeLocalStorageValues();
                if (iframe && result?.answer_key) {
                    PostMessage.sendAnswerKey(result?.answer_key);
                }
                if (result?.redirect?.auto_redirect) {
                    redirectToExternalUrl(result.redirect.url, result.redirect.keep_iframe);
                } else {
                    if (result?.answer_key) {
                        searchParams.set('akey', result.answer_key);
                    }
                    navigate({
                        pathname: getSurveyPath({path: 'success', surveyId}),
                        search: `?${createSearchParams(searchParams)}`,
                    });
                }
            })
            .catch((error) => {
                if (
                    error.details?.submitErrors &&
                    Array.isArray(error.details.submitErrors) &&
                    error.details.submitErrors.length > 0
                ) {
                    (error.details.submitErrors as SubmitError[]).forEach((item) => {
                        const commonMessage = errorMessageByType[item.type as ValidationType];
                        const message = commonMessage ? commonMessage : item.message;
                        let question = item.name;
                        if (item.series && item.index !== undefined && item.name) {
                            question = getSeriesQuestionSlug(item.series, item.index, item.name);
                        }
                        methods.setError(question ?? 'root.serverError', {
                            message,
                            type: error.code,
                        });
                    });

                    const errorQuestion = (error.details.submitErrors as SubmitError[]).find(
                        (error) => Boolean(error.name),
                    )?.name;

                    if (errorQuestion) {
                        const errorPage = findErrorPage(errorQuestion, survey);
                        setPage(errorPage);
                    }
                }
            })
            .finally(() => {
                endSaving();
            });
    };

    const onChangePage = useCallback(
        (direction: 'next' | 'previous') => {
            const nextPageNumber = getNextPage(
                direction,
                currentPageNumber,
                survey,
                matchConditions,
            );
            setPage(nextPageNumber);
        },
        [currentPageNumber, matchConditions, setPage, survey],
    );

    const onNext = useCallback(() => {
        setCanFocus(true);
        methods.trigger().then((isValid) => {
            if (!isValid) return;
            Metrika.goal(Metrika.GoalType.Next, userCounters.current);
            onChangePage('next');
            contentRef.current?.scrollIntoView({behavior: 'smooth'});
            contentRef.current?.focus();
        });
    }, [methods, onChangePage]);

    const onPrevious = useCallback(() => {
        methods.clearErrors();
        Metrika.goal(Metrika.GoalType.Prev, userCounters.current);
        onChangePage('previous');
    }, [methods, onChangePage]);

    const onError = useCallback(() => {
        setCanFocus(true);
    }, []);

    if (!prepared) {
        return (
            <div className={cnSurveyPage('Loading')}>
                <Loader size="l" />
            </div>
        );
    }

    if (isEmpty(survey)) {
        return null;
    }

    const nextPageNumber = getNextPage('next', currentPageNumber, survey, matchConditions);

    const isFirstPage = currentPageNumber === 1;
    const isLastPage =
        !survey.pages?.length ||
        currentPageNumber === survey.pages?.length ||
        nextPageNumber > survey.pages?.length;

    return (
        <FormProvider {...methods}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <form
                onSubmit={methods.handleSubmit(isLastPage ? onSubmit : onNext)}
                className={cnSurveyPage('', {iframe, hasRequired})}
                onError={onError} // causes eslint a11y error
                autoComplete="off"
            >
                {!iframe && (
                    <MarkdownText
                        variant="display-3"
                        as="h1"
                        className={cnSurveyPage('Name')}
                        text={survey.name}
                    />
                )}
                <Card
                    type="container"
                    view={
                        [Theme.Dark, Theme.DarkHC].includes(theme as Theme) ? 'outlined' : 'raised'
                    }
                    className={cnSurveyPage('Content', {theme, mobile, hasRequired})}
                    ref={contentRef}
                >
                    {page?.items.map((item) => renderItem(item, matchConditions, watchItems))}
                    <div aria-live="assertive">
                        {!isEmpty(methods.formState.errors) && (
                            <Text
                                variant="body-1"
                                color="danger"
                                className={cnSurveyPage('Error')}
                                as="div"
                            >
                                {methods.formState.errors.root?.serverError?.message ??
                                    i18n('survey-page', 'any-error')}
                            </Text>
                        )}
                    </div>
                    <div className={cnSurveyPage('Buttons')}>
                        {!isFirstPage && (
                            <Button
                                view="flat"
                                size="l"
                                onClick={onPrevious}
                                className={cnSurveyPage('Button')}
                            >
                                <MarkdownText text={survey.texts.back} />
                            </Button>
                        )}
                        {!isLastPage && (
                            <Button
                                view="action"
                                size="l"
                                onClick={onNext}
                                className={cnSurveyPage('Button')}
                            >
                                <MarkdownText text={survey.texts.next} />
                            </Button>
                        )}
                        {isLastPage && matchConditions({conditions: survey.conditions}) && (
                            <Button
                                type="submit"
                                view="action"
                                size="l"
                                className={cnSurveyPage('Button')}
                                loading={methods.formState.isSubmitting || saving}
                            >
                                <MarkdownText text={survey.texts.submit} />
                            </Button>
                        )}
                    </div>
                </Card>
            </form>
        </FormProvider>
    );
};
export default SurveyPage;
